import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  count?: number;
}

interface NavigationProps {
  onToggle?: (isOpen: boolean) => void;
}

export default function Navigation({ onToggle }: NavigationProps) {
  const location = useLocation();
  const [counts] = useState<Record<string, number>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    { id: 'bookmarks', label: '书签', icon: '🔖', path: '/bookmarks' },
    { id: 'notes', label: '笔记', icon: '📝', path: '/notes' },
    { id: 'expenses', label: '记账', icon: '💰', path: '/expenses' },
    { id: 'schedule', label: '日程', icon: '📅', path: '/schedule' },
    { id: 'reminders', label: '提醒', icon: '⏰', path: '/reminders' },
    { id: 'categories', label: '分类管理', icon: '📁', path: '/categories' },
  ];

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={handleMobileMenuToggle}
        className="mobile-menu-button"
      >
        <span className="menu-icon">{isMobileMenuOpen ? '✕' : '☰'}</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={handleMobileMenuToggle}
        />
      )}

      {/* Desktop & Mobile Navigation */}
      <nav className={`navigation ${isOpen ? 'expanded' : 'collapsed'} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="nav-container">
          {/* Header */}
          <div className="nav-header">
            {isOpen && (
              <h1 className="nav-title gradient-text">Notes</h1>
            )}
            <button
              onClick={handleToggle}
              className="nav-toggle"
              title={isOpen ? '收起' : '展开'}
            >
              <span className="toggle-icon">{isOpen ? '◀' : '▶'}</span>
            </button>
          </div>

          {/* Navigation Items */}
          <div className="nav-items">
            {navigationItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={handleMobileNavClick}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  title={!isOpen && !isMobileMenuOpen ? item.label : undefined}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {(isOpen || isMobileMenuOpen) && (
                    <>
                      <span className="nav-label">{item.label}</span>
                      {counts[item.id] !== undefined && (
                        <span className="nav-count">
                          {counts[item.id]}
                        </span>
                      )}
                    </>
                  )}
                  {isActive && <div className="nav-indicator"></div>}
                </Link>
              );
            })}
          </div>

          {/* Footer */}
          <div className="nav-footer">
            <Link
              to="/settings"
              onClick={handleMobileNavClick}
              className={`nav-item ${location.pathname === '/settings' ? 'active' : ''}`}
              title={!isOpen && !isMobileMenuOpen ? '设置' : undefined}
            >
              <span className="nav-icon">⚙️</span>
              {(isOpen || isMobileMenuOpen) && <span className="nav-label">设置</span>}
            </Link>
          </div>
        </div>

        <style>{`
        .mobile-menu-button {
          position: fixed;
          top: var(--spacing-md);
          left: var(--spacing-md);
          z-index: 50;
          padding: var(--spacing-sm);
          background: var(--color-bg-primary);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-lg);
          border: none;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        @media (min-width: 1024px) {
          .mobile-menu-button {
            display: none;
          }
        }

        .mobile-menu-button:hover {
          box-shadow: var(--shadow-xl);
          transform: scale(1.05);
        }

        .menu-icon {
          font-size: 24px;
          display: block;
        }

        .mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 40;
          backdrop-filter: blur(4px);
        }

        @media (min-width: 1024px) {
          .mobile-overlay {
            display: none;
          }
        }

        .navigation {
          background: linear-gradient(180deg, var(--color-bg-primary) 0%, var(--color-gray-50) 100%);
          border-right: 1px solid var(--color-gray-200);
          transition: all var(--transition-slow);
          height: 100vh;
          position: sticky;
          top: 0;
          z-index: 40;
          box-shadow: var(--shadow-md);
        }

        .navigation.collapsed {
          width: 64px;
        }

        .navigation.expanded {
          width: 256px;
        }

        @media (max-width: 1023px) {
          .navigation {
            display: none;
          }

          .navigation.mobile-open {
            display: block;
            position: fixed;
            left: 0;
            top: 0;
            width: 256px;
          }
        }

        .nav-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .nav-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--color-gray-200);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
        }

        .nav-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-toggle {
          padding: var(--spacing-sm);
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all var(--transition-base);
          color: white;
        }

        .nav-toggle:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .toggle-icon {
          font-size: 16px;
        }

        .nav-items {
          flex: 1;
          overflow-y: auto;
          padding: var(--spacing-md);
        }

        .nav-item {
          display: flex;
          align-items: center;
          padding: var(--spacing-sm) var(--spacing-md);
          margin-bottom: var(--spacing-xs);
          border-radius: var(--radius-md);
          text-decoration: none;
          color: var(--color-text-primary);
          transition: all var(--transition-base);
          position: relative;
          overflow: hidden;
        }

        .nav-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          transform: scaleY(0);
          transition: transform var(--transition-base);
        }

        .nav-item:hover {
          background: var(--color-gray-100);
          transform: translateX(4px);
        }

        .nav-item:hover::before {
          transform: scaleY(1);
        }

        .nav-item.active {
          background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-secondary-light) 100%);
          color: var(--color-primary);
          font-weight: 600;
        }

        .nav-item.active::before {
          transform: scaleY(1);
        }

        .nav-icon {
          font-size: 24px;
          flex-shrink: 0;
          transition: transform var(--transition-base);
        }

        .nav-item:hover .nav-icon {
          transform: scale(1.2);
        }

        .nav-label {
          margin-left: var(--spacing-sm);
          font-weight: 500;
          white-space: nowrap;
        }

        .nav-count {
          margin-left: auto;
          font-size: 12px;
          color: var(--color-text-tertiary);
          background: var(--color-gray-200);
          padding: 2px 8px;
          border-radius: var(--radius-full);
          font-weight: 600;
        }

        .nav-item.active .nav-count {
          background: var(--color-primary);
          color: white;
        }

        .nav-indicator {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 60%;
          background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          border-radius: var(--radius-full) 0 0 var(--radius-full);
        }

        .nav-footer {
          border-top: 1px solid var(--color-gray-200);
          padding: var(--spacing-md);
          flex-shrink: 0;
          background: var(--color-bg-primary);
        }
      `}</style>
      </nav>
    </>
  );
}
